// Copyright (c) StrangeBee 2023

import React, { ReactElement, useMemo } from 'react';
import { similarityFilters } from '@Constants/settings';
import { useGetSettings } from '@Queries/UISettingsQueries';
import { openMessage, useLocalMessage } from '@Helpers/';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { THLayout } from '@Molecules/THLayout';
import { THForm } from '@Molecules/THForm/THForm';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import useValidateForm from '@Hooks/useValidateForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { PermissionsEnum } from 'thehive-sdk';
import client from '@Client';
import { ICONS } from '@Constants/iconsApp';
import usePageTitle from '@Hooks/usePageTitle';
import { useParams } from 'react-router';

import './settings.scss';

const localSettingsOptionsRoot = 'settings.form.defaultAlertSimilarCaseFilter.options.';

const Settings = (): ReactElement => {
    const i = useLocalMessage();
    const { orgId } = useParams<{ orgId: string }>();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();
    const { data: settingsData, refetch: refetchSettings } = useGetSettings();
    const selectValueSimilarity = useMemo(
        () =>
            Object.keys(similarityFilters).map((key) => ({
                label: i(`${localSettingsOptionsRoot}${key}`),
                value: key,
            })),
        [i],
    );

    const submitHandler = (data: any): void => {
        client.UISettingsController.multiEdit(data)
            .then(() => {
                openMessage('success', i('settings.actions.update.success'));
            })
            .catch(() => {
                openMessage('error', i('settings.actions.update.error'));
            })
            .finally(() => {
                refetchSettings().then(() => {
                    form.resetFields();
                    setFieldsTouched(false);
                });
            });
    };

    usePageTitle(i('page-title.organisation.settings', { companyName: orgId }));

    return (
        <THLayout>
            <THLayout.Content>
                {settingsData && (
                    <THForm
                        name="settings-form"
                        form={form}
                        onFinish={submitHandler}
                        onValuesChange={() => {
                            onFormValuesChange();
                            setFieldsTouched(true);
                        }}
                        initialValues={settingsData}
                        className="settings-form"
                    >
                        <THForm.Layout
                            footer={
                                <FormSubmitButton
                                    warning={fieldsTouched}
                                    onCancel={() => {
                                        form.resetFields();
                                        setFieldsTouched(false);
                                    }}
                                    disabled={!isValid}
                                    hidden={!fieldsTouched}
                                />
                            }
                        >
                            <THForm.Item
                                horizontal
                                label={i('settings.form.hideEmptyCaseButton.label')}
                                valuePropName="checked"
                                name="hideEmptyCaseButton"
                                help={i('settings.form.hideEmptyCaseButton.description')}
                            >
                                <THSwitch />
                            </THForm.Item>

                            <THForm.Item
                                horizontal
                                label={i('settings.form.disallowMergeAlertInResolvedSimilarCases.label')}
                                valuePropName="checked"
                                name="disallowMergeAlertInResolvedSimilarCases"
                                help={i('settings.form.disallowMergeAlertInResolvedSimilarCases.description')}
                            >
                                <THSwitch />
                            </THForm.Item>

                            <THForm.Item
                                horizontal
                                label={i('settings.form.disallowRefreshInDashboards.label')}
                                valuePropName="checked"
                                name="disallowRefreshInDashboards"
                                help={i('settings.form.disallowRefreshInDashboards.description')}
                            >
                                <THSwitch />
                            </THForm.Item>

                            <THForm.Item
                                horizontal
                                label={i('settings.form.disallowPeriodAllInDashboards.label')}
                                valuePropName="checked"
                                name="disallowPeriodAllInDashboards"
                                help={i('settings.form.disallowPeriodAllInDashboards.description')}
                            >
                                <THSwitch />
                            </THForm.Item>

                            <THForm.Item
                                horizontal
                                label={i('settings.form.disableCacheOption.label')}
                                valuePropName="checked"
                                name="disableCacheOption"
                                help={i('settings.form.disableCacheOption.description')}
                            >
                                <THSwitch />
                            </THForm.Item>

                            <THForm.Item
                                label={i('settings.form.defaultAlertSimilarCaseFilter.label')}
                                name="defaultAlertSimilarCaseFilter"
                            >
                                <THSelect options={selectValueSimilarity} />
                            </THForm.Item>
                        </THForm.Layout>
                    </THForm>
                )}
            </THLayout.Content>
        </THLayout>
    );
};

const pathUser = RouteBuilder.compute('organisation.settings');

export const Route: RouteType = {
    menu: {
        label: 'organisation.layout.nav-bar.settings',
        icon: <ICONS.SettingIcon />,
    },
    permissionNeeded: [PermissionsEnum.MANAGE_CONFIG],
    path: pathUser,
    content: <Settings />,
    className: 'settings',
    shared: true,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.organisation.settings' },
};
